import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useLanguage } from "./LanguageContext";

const LANG_BCP47: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  hi: "hi-IN",
  sr: "sr-RS",
};

function pickVoice(
  voices: SpeechSynthesisVoice[],
  langCode: string,
): SpeechSynthesisVoice | null {
  if (langCode !== "en") {
    // Prefer an exact language match (e.g. hi-IN for Hindi)
    const exact = voices.find((v) =>
      v.lang.toLowerCase().startsWith(langCode.toLowerCase()),
    );
    if (exact) return exact;
    // Fall through — rely on utterance.lang to guide the browser's default
    return null;
  }
  const preferred = [
    "Samantha",
    "Karen",
    "Moira",
    "Google UK English Female",
    "Google US English",
    "Microsoft Zira",
  ];
  for (const name of preferred) {
    const found = voices.find((v) => v.name.includes(name));
    if (found) return found;
  }
  return voices.find((v) => v.lang.startsWith("en")) ?? voices[0] ?? null;
}

interface SpeechContextValue {
  speak: (text: string) => void;
  toggle: () => void;
  enabled: boolean;
  isSupported: boolean;
}

const SpeechContext = createContext<SpeechContextValue>({
  speak: () => {},
  toggle: () => {},
  enabled: true,
  isSupported: false,
});

export const SpeechProvider = ({ children }: { children: ReactNode }) => {
  const isSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const lastTextRef = useRef<string>("");
  const lastLangRef = useRef<string>("en");
  const [enabled, setEnabled] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    if (!isSupported) return;
    const update = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
    };
    update();
    window.speechSynthesis.addEventListener("voiceschanged", update);
    return () =>
      window.speechSynthesis.removeEventListener("voiceschanged", update);
  }, [isSupported]);

  const _speak = useCallback(
    (text: string, lang: string) => {
      if (!isSupported) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = LANG_BCP47[lang] ?? "en-US";
      const voice = pickVoice(voicesRef.current, lang);
      if (voice) utterance.voice = voice;
      utterance.rate = 0.92;
      window.speechSynthesis.speak(utterance);
    },
    [isSupported],
  );

  const speak = useCallback(
    (text: string) => {
      lastTextRef.current = text;
      lastLangRef.current = language;
      if (enabled) _speak(text, language);
    },
    [_speak, enabled, language],
  );

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (!next) {
        window.speechSynthesis?.cancel();
      } else if (lastTextRef.current) {
        _speak(lastTextRef.current, lastLangRef.current);
      }
      return next;
    });
  }, [_speak]);

  return (
    <SpeechContext.Provider value={{ speak, toggle, enabled, isSupported }}>
      {children}
    </SpeechContext.Provider>
  );
};

export const useSpeechContext = () => useContext(SpeechContext);
